import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { LoginRequest, OrgMembership, UserProfile } from '@todo/shared';
import { resetPickerForNextLogin } from '../components/ModulePicker';
import { enableWebPush, resetWebPushRegistration } from '../lib/push';
import { notificationApi } from '../api/notification.api';
import { currentInstallationId } from '../lib/device';
import { authApi } from '../api/auth.api';
import { setSessionExpiredHandler } from '../api/client';
import { clearTokens, getAccessToken, storeTokens } from '../api/tokens';

/**
 * Who is logged in, for the whole app.
 *
 * Deliberately small: it owns the user object and the login/logout actions and
 * nothing else. Feature data (tasks, users) belongs in the components or hooks
 * that need it, not in a global store.
 */

export interface AuthState {
  user: UserProfile | null;
  /** Every organisation the signed-in account can enter (>= 1 entry when
   *  signed in). More than one means the sidebar shows the org switcher. */
  memberships: OrgMembership[];
  /** true only while the initial "do I have a valid token?" check runs. */
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (credentials: LoginRequest) => Promise<UserProfile>;
  /** Swap the session to another organisation, then HARD-RELOAD the app so
   *  no cached data from the previous organisation survives. */
  switchOrg: (orgId: string) => Promise<void>;
  logout: () => void;
  refreshProfile: () => Promise<void>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthState | undefined>(undefined);

/** Memberships survive a page reload here (GET /auth/me does not resend
 *  them). Org names only - nothing sensitive - and re-written on every
 *  login and switch, so it can never be fresher than the last sign-in. */
const MEMBERSHIPS_KEY = 'todo.memberships';

function readStoredMemberships(): OrgMembership[] {
  try {
    const raw = localStorage.getItem(MEMBERSHIPS_KEY);
    return raw ? (JSON.parse(raw) as OrgMembership[]) : [];
  } catch {
    return [];
  }
}

function storeMemberships(memberships: OrgMembership[]): void {
  try {
    localStorage.setItem(MEMBERSHIPS_KEY, JSON.stringify(memberships));
  } catch {
    /* storage unavailable - the switcher just hides until next login */
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [memberships, setMemberships] = useState<OrgMembership[]>(readStoredMemberships);
  const [isLoading, setIsLoading] = useState(true);

  const logout = useCallback(() => {
    // Say goodbye BEFORE the token disappears: release this browser's push
    // registration so a signed-out machine stops receiving banners. Fire and
    // forget - a failed goodbye must never block the logout itself.
    notificationApi.unregisterDevice(currentInstallationId()).catch(() => undefined);
    resetWebPushRegistration();
    setUser(null);
    setMemberships([]);
    // The module picker must greet every sign-in, including the next one
    // in this same tab.
    resetPickerForNextLogin();
    try { localStorage.removeItem(MEMBERSHIPS_KEY); } catch { /* fine */ }
    // Clear one tick later so the in-flight goodbye still carries its
    // Authorization header (the interceptor reads it when the request runs).
    setTimeout(clearTokens, 0);
  }, []);

  /**
   * On mount, decide whether the stored token is still usable.
   *
   * There is no way to know locally - a token can be revoked or the account
   * deactivated - so we ask the server. GET /auth/me either returns the
   * profile or 401s, and the client's interceptor will have already tried a
   * refresh before we see the failure.
   */
  useEffect(() => {
    let cancelled = false;

    async function restoreSession() {
      if (!getAccessToken()) {
        setIsLoading(false);
        return;
      }

      try {
        const profile = await authApi.me();
        if (!cancelled) {
          setUser(profile);
          void enableWebPush();
        }
      } catch {
        if (!cancelled) {
          clearTokens();
          setUser(null);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    void restoreSession();

    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Let the HTTP layer tell us the session died.
   *
   * The interceptor cannot import this context (React state lives in the
   * component tree), so it calls a handler we register here. Inversion of
   * control, and it keeps client.ts free of any React dependency.
   */
  useEffect(() => {
    setSessionExpiredHandler(logout);
    return () => setSessionExpiredHandler(null);
  }, [logout]);

  const login = useCallback(async (credentials: LoginRequest) => {
    const response = await authApi.login(credentials);
    storeTokens(response.tokens);
    setUser(response.user);
    setMemberships(response.memberships);
    storeMemberships(response.memberships);
    void enableWebPush(); // fire-and-forget: pushes reach this browser from now on
    return response.user;
  }, []);

  /**
   * Switch to another organisation: fresh tokens for that membership, then a
   * FULL RELOAD. The reload is deliberate - React Query caches, the chat
   * widget history and every open page still hold the previous
   * organisation's data, and a hard restart is the one certain way none of
   * it leaks across.
   */
  const switchOrg = useCallback(async (orgId: string) => {
    const response = await authApi.switchOrg({ orgId });
    storeTokens(response.tokens);
    storeMemberships(response.memberships);
    window.location.href = '/';
  }, []);

  const refreshProfile = useCallback(async () => {
    const profile = await authApi.me();
    setUser(profile);
  }, []);

  /**
   * useMemo so the context value is a stable object.
   *
   * Without it a new object is created on every render and EVERY consumer
   * re-renders, even ones that only read `user`.
   */
  const value = useMemo<AuthState>(
    () => ({
      user,
      memberships,
      isLoading,
      isAuthenticated: user !== null,
      login,
      switchOrg,
      logout,
      refreshProfile,
    }),
    [user, memberships, isLoading, login, switchOrg, logout, refreshProfile],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
